import React, { useState, useEffect, useCallback, useRef } from 'react';
import SensitiveContent from './SensitiveContent';
import { useUser } from '../context/UserContext';
import {
    getBiometricInfo,
    authenticate,
    createBiometricProof,
    getBiometricLabel,
    normalizeBiometricType,
    isAccessGranted,
    openBiometricSettings,
    getTgPlatform,
    subscribeBiometricManagerUpdated,
    isBiometricTypeAllowedForAccountMode,
} from '../services/biometricService';

const apiUrl = process.env.REACT_APP_API_URL
    || (typeof window !== 'undefined' ? `${window.location.origin}/api` : 'http://localhost:8000/api');
const PASSCODE_LENGTH = 6;

const LockScreen = ({ user, onUnlock }) => {
    const { blockSession } = useUser();
    const [digits, setDigits] = useState('');
    const [error, setError] = useState('');
    const [shake, setShake] = useState(false);
    const [loading, setLoading] = useState(false);
    const [lockSeconds, setLockSeconds] = useState(0);
    const [dismissing, setDismissing] = useState(false);
    const [biometricAvailable, setBiometricAvailable] = useState(false);
    const [biometricType, setBiometricType] = useState(null);
    const lockTimerRef = useRef(null);
    const autoBiometricAttemptedRef = useRef(false);

    const [panicModalOpen, setPanicModalOpen] = useState(false);
    const [panicCode, setPanicCode] = useState('');
    const [panicError, setPanicError] = useState('');
    const [panicLoading, setPanicLoading] = useState(false);
    /** Length of PIN entered in «личный кабинет» after successful panic-activate (same session); used to auto-submit non-6 emergency PINs */
    const [panicArmedPinLength, setPanicArmedPinLength] = useState(0);
    const [biometricSettingsHint, setBiometricSettingsHint] = useState(false);

    const maxInputLength = panicArmedPinLength > 0
        ? Math.max(PASSCODE_LENGTH, panicArmedPinLength)
        : PASSCODE_LENGTH;

    const refreshBiometricInfo = useCallback(() => (
        getBiometricInfo().then(({ available, type }) => {
            setBiometricAvailable(available);
            setBiometricType(type);
        })
    ), []);

    useEffect(() => {
        let mounted = true;
        getBiometricInfo().then(({ available, type }) => {
            if (!mounted) return;
            setBiometricAvailable(available);
            setBiometricType(type);
        });
        return () => { mounted = false; };
    }, []);

    useEffect(() => {
        const unsub = subscribeBiometricManagerUpdated(() => {
            refreshBiometricInfo();
        });
        return unsub;
    }, [refreshBiometricInfo]);

    useEffect(() => {
        if (lockSeconds <= 0) return;
        lockTimerRef.current = setInterval(() => {
            setLockSeconds((prev) => {
                if (prev <= 1) {
                    clearInterval(lockTimerRef.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(lockTimerRef.current);
    }, [lockSeconds]);

    const triggerShake = useCallback(() => {
        setShake(true);
        setTimeout(() => setShake(false), 500);
    }, []);

    const verifyWithBackend = useCallback(async (type, value, currentBiometricType = null, challenge = null) => {
        setLoading(true);
        setError('');
        setBiometricSettingsHint(false);
        try {
            const payload = { telegramId: user.telegramId, type, value };
            if (currentBiometricType) payload.biometricType = normalizeBiometricType(currentBiometricType);
            if (challenge) payload.challenge = challenge;

            const resp = await fetch(`${apiUrl}/front/app-lock/verify`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            const data = await resp.json();
            if (data.accountBlocked) {
                blockSession(data.blockReason || 'Аккаунт заблокирован.');
                return;
            }
            if (data.success) {
                setDismissing(true);
                setTimeout(() => onUnlock(), 200);
                return;
            }
            if (data.lockSeconds) {
                setLockSeconds(data.lockSeconds);
            }
            triggerShake();
            setDigits('');
            setError(data.error || 'Неверный код-пароль');
        } catch {
            setError('Ошибка соединения');
            triggerShake();
            setDigits('');
        } finally {
            setLoading(false);
        }
    }, [user, onUnlock, triggerShake, blockSession]);

    const fetchBiometricChallenge = useCallback(async () => {
        const resp = await fetch(`${apiUrl}/front/app-lock/biometric-challenge`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ telegramId: user.telegramId }),
        });
        const data = await resp.json();
        if (!resp.ok || !data?.success || !data?.challenge) return null;
        return data.challenge;
    }, [user.telegramId]);

    const handleBiometric = useCallback(async () => {
        const selectedMode = user?.appLock?.biometricMode;
        const isAllowedType = isBiometricTypeAllowedForAccountMode(selectedMode, biometricType);
        if (!isAllowedType) {
            const modeNorm = String(selectedMode || '').toLowerCase();
            const onlyFace = modeNorm.includes('face');
            setError(onlyFace ? 'Разрешен только Face ID' : 'Разрешен только отпечаток / Touch ID');
            triggerShake();
            return;
        }

        const label = getBiometricLabel(biometricType);
        const result = await authenticate(`Разблокировка через ${label}`);
        if (result.success && result.token) {
            const challenge = await fetchBiometricChallenge();
            const proof = challenge ? await createBiometricProof(result.token, challenge) : null;
            if (!challenge || !proof) {
                setError('Не удалось подготовить проверку биометрии. Используйте PIN.');
                triggerShake();
                return;
            }
            await verifyWithBackend('biometric', proof, biometricType, challenge);
        } else if (!isAccessGranted()) {
            setBiometricSettingsHint(true);
        }
    }, [biometricType, verifyWithBackend, user?.appLock?.biometricMode, triggerShake, fetchBiometricChallenge]);

    useEffect(() => {
        if (autoBiometricAttemptedRef.current) return;
        if (!biometricAvailable || !user?.appLock?.biometricEnabled) return;
        if (lockSeconds > 0 || loading) return;

        // iOS Telegram clients often require a direct user gesture for biometric prompt.
        // Auto-attempt can silently fail there, so we skip it on iOS.
        const platform = getTgPlatform();
        if (platform === 'ios') return;

        autoBiometricAttemptedRef.current = true;
        const timer = setTimeout(() => {
            handleBiometric();
        }, 150);

        return () => clearTimeout(timer);
    }, [biometricAvailable, user?.appLock?.biometricEnabled, lockSeconds, loading, handleBiometric]);

    useEffect(() => {
        if (!digits.length) return;
        const atSix = digits.length === PASSCODE_LENGTH;
        const atArmedPanicLen = panicArmedPinLength > 0
            && panicArmedPinLength !== PASSCODE_LENGTH
            && digits.length === panicArmedPinLength;
        if (atSix || atArmedPanicLen) {
            verifyWithBackend('passcode', digits);
        }
    }, [digits, panicArmedPinLength, verifyWithBackend]);

    const handleDigit = useCallback((d) => {
        if (loading || lockSeconds > 0) return;
        setError('');
        setDigits((prev) => (prev.length < maxInputLength ? prev + d : prev));
    }, [loading, lockSeconds, maxInputLength]);

    const handleBackspace = useCallback(() => {
        if (loading || lockSeconds > 0) return;
        setDigits((prev) => prev.slice(0, -1));
    }, [loading, lockSeconds]);

    useEffect(() => {
        const onKeyDown = (e) => {
            if (loading || lockSeconds > 0 || panicModalOpen) return;
            if (e.key >= '0' && e.key <= '9') {
                e.preventDefault();
                handleDigit(e.key);
            } else if (e.key === 'Backspace') {
                e.preventDefault();
                handleBackspace();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [loading, lockSeconds, handleDigit, handleBackspace, panicModalOpen]);

    const handlePanicSubmit = async () => {
        if (!panicCode.trim()) return;
        setPanicLoading(true);
        setPanicError('');
        try {
            const resp = await fetch(`${apiUrl}/front/app-lock/panic-activate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ telegramId: user.telegramId, panicCode: panicCode.trim() }),
            });
            const data = await resp.json();
            if (data.activated) {
                setPanicArmedPinLength(panicCode.trim().length);
                setPanicModalOpen(false);
                setPanicCode('');
            } else {
                setPanicError('Неверный номер аккаунта');
            }
        } catch {
            setPanicError('Ошибка соединения');
        } finally {
            setPanicLoading(false);
        }
    };

    const formatTimer = (sec) => {
        const m = Math.floor(sec / 60);
        const s = sec % 60;
        return `${m}:${String(s).padStart(2, '0')}`;
    };

    const biometricIcon = (
        <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
            <path d="M9 11.75A1.25 1.25 0 1 0 9 9.25a1.25 1.25 0 0 0 0 2.5zm6 0a1.25 1.25 0 1 0 0-2.5 1.25 1.25 0 0 0 0 2.5zm-3 5.5c2.33 0 4.32-1.45 5.12-3.5h-1.67c-.69 1.19-1.97 2-3.45 2s-2.75-.81-3.45-2H6.88c.8 2.05 2.79 3.5 5.12 3.5zM12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
        </svg>
    );

    return (
        <SensitiveContent>
            <div className={`lock-screen ${dismissing ? 'lock-screen-dismiss' : ''}`}>

                <button
                    className="lock-account-btn"
                    onClick={() => {
                        setPanicModalOpen(true);
                        setPanicError('');
                        setPanicCode('');
                    }}
                >
                    <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
                        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                    </svg>
                </button>

                <div className="lock-screen-content">
                    <div className="lock-icon">
                        <svg viewBox="0 0 24 24" className="w-12 h-12" fill="currentColor">
                            <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM12 17c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1s3.1 1.39 3.1 3.1v2z"/>
                        </svg>
                    </div>

                    <h2 className="lock-title">
                        {lockSeconds > 0
                            ? `Повторите через ${formatTimer(lockSeconds)}`
                            : 'Введите код-пароль'}
                    </h2>

                    <div className={`lock-dots ${shake ? 'lock-shake' : ''}`}>
                        {Array.from({ length: maxInputLength }).map((_, i) => (
                            <div
                                key={i}
                                className={`lock-dot ${i < digits.length ? 'lock-dot-filled' : ''}`}
                            />
                        ))}
                    </div>

                    {error && <p className="lock-error">{error}</p>}
                    {biometricSettingsHint && (
                        <button
                            className="text-gray-400 text-sm underline mt-1"
                            onPointerDown={(e) => {
                                e.preventDefault();
                                openBiometricSettings();
                            }}
                        >
                            Открыть настройки биометрии Telegram
                        </button>
                    )}

                    <div className="lock-keypad">
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => (
                            <button
                                key={d}
                                className="lock-key"
                                onPointerDown={(e) => { e.preventDefault(); handleDigit(String(d)); }}
                                disabled={loading || lockSeconds > 0}
                            >
                                {d}
                            </button>
                        ))}
                        <button
                            className="lock-key lock-key-action"
                            onPointerDown={biometricAvailable && user?.appLock?.biometricEnabled ? (e) => { e.preventDefault(); handleBiometric(); } : undefined}
                            disabled={loading || lockSeconds > 0 || !biometricAvailable || !user?.appLock?.biometricEnabled}
                            style={{
                                visibility: biometricAvailable && user?.appLock?.biometricEnabled ? 'visible' : 'hidden',
                            }}
                        >
                            {biometricIcon}
                        </button>
                        <button
                            className="lock-key"
                            onPointerDown={(e) => { e.preventDefault(); handleDigit('0'); }}
                            disabled={loading || lockSeconds > 0}
                        >
                            0
                        </button>
                        <button
                            className="lock-key lock-key-action"
                            onPointerDown={(e) => { e.preventDefault(); handleBackspace(); }}
                            disabled={loading || lockSeconds > 0 || digits.length === 0}
                        >
                            <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
                                <path d="M22 3H7c-.69 0-1.23.35-1.59.88L0 12l5.41 8.11c.36.53.9.89 1.59.89h15c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-3 12.59L17.59 17 14 13.41 10.41 17 9 15.59 12.59 12 9 8.41 10.41 7 14 10.59 17.59 7 19 8.41 15.41 12 19 15.59z"/>
                            </svg>
                        </button>
                    </div>
                </div>

                {panicModalOpen && (
                    <div className="panic-overlay" onClick={() => setPanicModalOpen(false)}>
                        <div className="panic-modal" onClick={(e) => e.stopPropagation()}>
                            <h3 className="panic-modal-title">Личный кабинет</h3>
                            <input
                                className="panic-modal-input"
                                type="text"
                                inputMode="numeric"
                                placeholder="Номер аккаунта"
                                value={panicCode}
                                onChange={(e) => setPanicCode(e.target.value)}
                                autoFocus
                                onKeyDown={(e) => { if (e.key === 'Enter') handlePanicSubmit(); }}
                            />
                            {panicError && <p className="panic-modal-error">{panicError}</p>}
                            <button
                                className="panic-modal-btn"
                                onClick={handlePanicSubmit}
                                disabled={panicLoading || !panicCode.trim()}
                            >
                                {panicLoading ? 'Загрузка...' : 'Войти'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </SensitiveContent>
    );
};

export default LockScreen;
